/**
 * 公告滚动工具类
 * 支持无限滚动和定时滚动两种模式
 */
class AnnouncementScroll {
  constructor(container, options = {}) {
    this.container =
      typeof container === "string"
        ? document.querySelector(container)
        : container;

    if (!this.container) {
      throw new Error("容器元素不存在");
    }

    // 默认配置
    this.config = {
      mode: "infinite", // 滚动模式: 'infinite' | 'interval'
      speed: 30, // 滚动速度 (px/s)
      direction: "horizontal", // 滚动方向: 'horizontal' | 'vertical'
      pauseOnHover: false, // 鼠标悬停时是否暂停
      interval: 300000, // 定时模式下的间隔时间 (ms)，默认5分钟
      gap: 20, // 公告之间的间距
      opacity: 1, // 透明度
      color: "#333333", // 文字颜色
      fontSize: "14px", // 字体大小
      // 显示范围设置
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      ...options,
    };

    this.isRunning = false;
    this.animationId = null;
    this.startTime = 0;
    this.pausedTime = 0;
    this.totalPausedTime = 0;
    this.intervalTimer = null;
    this.currentAnnouncementIndex = 0;

    // 保存原始内容
    this.originalContent = this.container.innerHTML;
    this.announcements = this.config.announcements || [];

    this.init();
  }

  /**
   * 初始化公告滚动
   */
  init() {
    this.setupContainer();
    this.setupContent();
    this.bindEvents();
    this.setupObservers();
  }

  /**
   * 设置容器样式
   */
  setupContainer() {
    const { direction } = this.config;

    this.container.style.overflow = "hidden";
    this.container.style.position = "relative";

    if (direction === "horizontal") {
      // 不设置whiteSpace，避免影响子元素
      this.container.style.display = "block"; // 确保容器是块级元素
    } else {
      this.container.style.height = this.container.offsetHeight + "px";
    }
  }

  /**
   * 设置内容
   */
  setupContent() {
    const {
      direction,
      opacity,
      color,
      fontSize,
      top,
      left,
      right,
      bottom,
      backgroundColor,
    } = this.config;

    // 创建背景容器
    this.backgroundWrapper = document.createElement("div");
    this.backgroundWrapper.style.position = "absolute";
    this.backgroundWrapper.style.top = top + "px";
    this.backgroundWrapper.style.left = left + "px";
    this.backgroundWrapper.style.right = right + "px";
    this.backgroundWrapper.style.bottom = bottom + "px";
    this.backgroundWrapper.style.zIndex = "5";
    this.backgroundWrapper.style.pointerEvents = "none";
    this.backgroundWrapper.style.backgroundColor =
      backgroundColor || "transparent";

    // 创建公告滚动包装器
    this.scrollWrapper = document.createElement("div");
    this.scrollWrapper.style.position = "absolute";
    this.scrollWrapper.style.zIndex = "10";
    this.scrollWrapper.style.pointerEvents = "none";
    this.scrollWrapper.style.opacity = opacity;

    if (direction === "horizontal") {
      // 水平滚动：只设置top和left，让宽度由内容决定
      this.scrollWrapper.style.top = top + "px";
      this.scrollWrapper.style.left = left + "px";
      this.scrollWrapper.style.whiteSpace = "nowrap";
      this.scrollWrapper.style.width = "auto";
      this.scrollWrapper.style.minWidth = "auto";
      this.scrollWrapper.style.maxWidth = "none";
      this.scrollWrapper.style.height = "100%";

      // 根据模式设置不同的显示方式
      if (this.config.mode === "infinite") {
        this.scrollWrapper.style.display = "inline-block";
        this.scrollWrapper.style.verticalAlign = "middle"; // 垂直居中
        this.scrollWrapper.style.lineHeight = "100%"; // 行高设置为100%以配合verticalAlign
      } else {
        // 间隔模式使用flexbox实现垂直居中
        this.scrollWrapper.style.display = "flex";
        this.scrollWrapper.style.alignItems = "center";
        this.scrollWrapper.style.justifyContent = "flex-start";
      }
      // 不设置right和bottom，避免inset: 0的效果
    } else {
      // 垂直滚动：设置所有位置属性
      this.scrollWrapper.style.top = top + "px";
      this.scrollWrapper.style.left = left + "px";
      this.scrollWrapper.style.right = right + "px";
      this.scrollWrapper.style.bottom = bottom + "px";
      this.scrollWrapper.style.width = "100%";
      this.scrollWrapper.style.height = "100%";
    }

    // 创建公告内容
    this.createAnnouncementContent();

    // 先插入背景容器，再插入滚动容器
    this.container.appendChild(this.backgroundWrapper);
    this.container.appendChild(this.scrollWrapper);

    // 调试：检查元素宽度
    if (direction === "horizontal") {
      console.log("容器宽度:", this.container.offsetWidth);
      console.log("滚动包装器宽度:", this.scrollWrapper.offsetWidth);
      console.log("滚动包装器scrollWidth:", this.scrollWrapper.scrollWidth);
    }
  }

  /**
   * 创建公告内容
   */
  createAnnouncementContent() {
    const { direction, gap, color, fontSize } = this.config;

    if (this.announcements.length === 0) {
      return;
    }

    // 清空现有内容
    this.scrollWrapper.innerHTML = "";

    if (this.config.mode === "infinite") {
      // 无限滚动模式：显示所有公告
      this.announcements.forEach((announcement, index) => {
        const announcementElement = this.createAnnouncementElement(
          announcement,
          color,
          fontSize
        );
        this.scrollWrapper.appendChild(announcementElement);

        // 添加间距
        const spacer = document.createElement("span");
        spacer.style.display = "inline-block";
        spacer.style.width = direction === "horizontal" ? gap + "px" : "auto";
        spacer.style.height = direction === "vertical" ? gap + "px" : "100%";
        spacer.style.marginRight = direction === "horizontal" ? "0px" : "0px";
        spacer.style.marginBottom = direction === "vertical" ? "0px" : "0px";
        spacer.style.verticalAlign = "middle"; // 垂直居中
        this.scrollWrapper.appendChild(spacer);
      });
    } else {
      // 定时模式：只显示当前公告
      const currentAnnouncement =
        this.announcements[this.currentAnnouncementIndex];
      if (currentAnnouncement) {
        const announcementElement = this.createAnnouncementElement(
          currentAnnouncement,
          color,
          fontSize
        );
        this.scrollWrapper.appendChild(announcementElement);
      }
    }
  }

  /**
   * 创建单个公告元素
   */
  createAnnouncementElement(announcement, color, fontSize) {
    const element = document.createElement("span");
    element.textContent = announcement;
    element.style.display = "inline-block";
    element.style.whiteSpace = "nowrap";
    element.style.pointerEvents = "none";
    element.style.color = color;
    element.style.fontSize = fontSize;
    element.style.verticalAlign = "middle"; // 垂直居中
    return element;
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    if (this.config.pauseOnHover) {
      this.container.addEventListener(
        "mouseenter",
        this.handleMouseEnter.bind(this)
      );
      this.container.addEventListener(
        "mouseleave",
        this.handleMouseLeave.bind(this)
      );
    }
  }

  /**
   * 设置容器变化监听器
   */
  setupObservers() {
    // 监听容器尺寸变化
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === this.container) {
            this.handleContainerResize();
          }
        }
      });
      this.resizeObserver.observe(this.container);
    } else {
      // 降级方案：使用window resize事件
      this.containerResizeHandler = this.debounce(() => {
        this.handleContainerResize();
      }, 100);
      window.addEventListener("resize", this.containerResizeHandler);
    }
  }

  /**
   * 防抖函数
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * 处理容器尺寸变化
   */
  handleContainerResize() {
    this.setupContainer();
    if (this.isRunning) {
      this.startTime = performance.now() - this.totalPausedTime;
    }
  }

  /**
   * 鼠标进入事件
   */
  handleMouseEnter() {
    this.pause();
  }

  /**
   * 鼠标离开事件
   */
  handleMouseLeave() {
    this.resume();
  }

  /**
   * 开始滚动
   */
  start() {
    if (this.isRunning) return;

    this.isRunning = true;
    this.startTime = performance.now() - this.totalPausedTime;

    if (this.config.mode === "infinite") {
      this.animate();
    } else {
      this.startIntervalMode();
    }
  }

  /**
   * 开始定时模式
   */
  startIntervalMode() {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
    }

    // 立即开始滚动当前公告
    this.animate();

    // 设置定时器切换公告
    this.intervalTimer = setInterval(() => {
      this.switchToNextAnnouncement();
    }, this.config.interval);
  }

  /**
   * 切换到下一个公告
   */
  switchToNextAnnouncement() {
    this.currentAnnouncementIndex =
      (this.currentAnnouncementIndex + 1) % this.announcements.length;
    this.createAnnouncementContent();

    // 显示滚动包装器和背景容器并重新开始滚动
    if (this.scrollWrapper) {
      // 根据模式设置正确的display样式
      if (this.config.mode === "infinite") {
        this.scrollWrapper.style.display = "inline-block";
      } else {
        this.scrollWrapper.style.display = "flex";
      }
    }
    if (this.backgroundWrapper) {
      this.backgroundWrapper.style.display = "block";
    }

    this.startTime = performance.now() - this.totalPausedTime;
    this.isRunning = true;
    this.animate();
  }

  /**
   * 暂停滚动
   */
  pause() {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.pausedTime = performance.now();
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }

  /**
   * 恢复滚动
   */
  resume() {
    if (this.isRunning) return;

    this.totalPausedTime += performance.now() - this.pausedTime;
    this.start();
  }

  /**
   * 停止滚动
   */
  stop() {
    this.isRunning = false;
    this.totalPausedTime = 0;
    this.pausedTime = 0;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
      this.intervalTimer = null;
    }

    // 重置位置
    if (this.scrollWrapper) {
      this.scrollWrapper.style.transform = "translate(0, 0)";
    }
  }

  /**
   * 动画循环
   */
  animate() {
    if (!this.isRunning) return;

    const currentTime = performance.now();
    const elapsed =
      (currentTime - this.startTime - this.totalPausedTime) / 1000;
    const { direction, speed } = this.config;

    let translateX = 0;
    let translateY = 0;

    if (direction === "horizontal") {
      const containerWidth = this.container.offsetWidth;
      const { left, right } = this.config;
      const effectiveWidth = containerWidth - left - right;

      // 从右侧开始滚动：初始位置是容器宽度，然后向左移动
      const initialPosition = effectiveWidth;
      translateX = initialPosition - elapsed * speed;

      if (this.config.mode === "infinite") {
        // 无限滚动：当内容完全移出时重置位置
        const contentWidth =
          this.scrollWrapper.scrollWidth || this.scrollWrapper.offsetWidth;
        if (translateX <= -contentWidth) {
          this.startTime = currentTime - this.totalPausedTime;
          translateX = initialPosition;
        }
      } else {
        // 定时模式：当当前公告完全移出时隐藏内容，但不停止定时器
        const currentAnnouncementWidth =
          this.scrollWrapper.children[0]?.offsetWidth || 0;
        if (translateX <= -currentAnnouncementWidth) {
          // 隐藏滚动包装器和背景容器，但不调用stop()，保持定时器运行
          if (this.scrollWrapper) {
            this.scrollWrapper.style.display = "none";
          }
          if (this.backgroundWrapper) {
            this.backgroundWrapper.style.display = "none";
          }
          // 停止当前动画，但保持isRunning状态，等待定时器触发下一个
          this.isRunning = false;
          if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
          }
          return;
        }
      }
    } else {
      const containerHeight = this.container.offsetHeight;
      const { top, bottom } = this.config;
      const effectiveHeight = containerHeight - top - bottom;

      // 从底部开始滚动：初始位置是容器高度，然后向上移动
      const initialPosition = effectiveHeight;
      translateY = initialPosition - elapsed * speed;

      if (this.config.mode === "infinite") {
        // 无限滚动：当内容完全移出时重置位置
        const contentHeight =
          this.scrollWrapper.scrollHeight || this.scrollWrapper.offsetHeight;
        if (translateY <= -contentHeight) {
          this.startTime = currentTime - this.totalPausedTime;
          translateY = initialPosition;
        }
      } else {
        // 定时模式：当当前公告完全移出时隐藏内容，但不停止定时器
        const currentAnnouncementHeight =
          this.scrollWrapper.children[0]?.offsetHeight || 0;
        if (translateY <= -currentAnnouncementHeight) {
          // 隐藏滚动包装器和背景容器，但不调用stop()，保持定时器运行
          if (this.scrollWrapper) {
            this.scrollWrapper.style.display = "none";
          }
          if (this.backgroundWrapper) {
            this.backgroundWrapper.style.display = "none";
          }
          // 停止当前动画，但保持isRunning状态，等待定时器触发下一个
          this.isRunning = false;
          if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
          }
          return;
        }
      }
    }

    // 应用变换
    if (this.scrollWrapper) {
      this.scrollWrapper.style.transform = `translate(${translateX}px, ${translateY}px)`;
    }

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  /**
   * 销毁公告滚动
   */
  destroy() {
    this.stop();

    // 移除事件监听
    if (this.config.pauseOnHover) {
      this.container.removeEventListener("mouseenter", this.handleMouseEnter);
      this.container.removeEventListener("mouseleave", this.handleMouseLeave);
    }

    // 清理观察器
    this.cleanupObservers();

    // 移除公告滚动元素和背景容器
    if (this.scrollWrapper && this.scrollWrapper.parentNode) {
      this.container.removeChild(this.scrollWrapper);
      this.scrollWrapper = null;
    }
    if (this.backgroundWrapper && this.backgroundWrapper.parentNode) {
      this.container.removeChild(this.backgroundWrapper);
      this.backgroundWrapper = null;
    }
  }

  /**
   * 清理观察器
   */
  cleanupObservers() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.containerResizeHandler) {
      window.removeEventListener("resize", this.containerResizeHandler);
      this.containerResizeHandler = null;
    }
  }

  /**
   * 更新公告列表
   */
  updateAnnouncements(announcements) {
    this.announcements = announcements;
    this.currentAnnouncementIndex = 0;
    this.createAnnouncementContent();
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.stop();

    // 清理现有元素
    if (this.scrollWrapper && this.scrollWrapper.parentNode) {
      this.container.removeChild(this.scrollWrapper);
      this.scrollWrapper = null;
    }
    if (this.backgroundWrapper && this.backgroundWrapper.parentNode) {
      this.container.removeChild(this.backgroundWrapper);
      this.backgroundWrapper = null;
    }

    // 重新设置容器样式和内容
    this.setupContainer();
    this.setupContent();
  }
}

/**
 * 创建公告滚动的便捷方法
 * @param {string|HTMLElement} container - 容器元素或选择器
 * @param {Array} announcements - 公告列表
 * @param {Object} options - 配置选项
 * @returns {AnnouncementScroll} 公告滚动实例
 */
export function createAnnouncementScroll(
  container,
  announcements,
  options = {}
) {
  const instance = new AnnouncementScroll(container, {
    ...options,
    announcements,
  });
  return instance;
}

/**
 * 默认配置的公告滚动方法
 * @param {string|HTMLElement} container - 容器元素或选择器
 * @param {Array} announcements - 公告列表
 * @param {Object} options - 配置选项
 * @returns {AnnouncementScroll} 公告滚动实例
 */
export function announcementScroll(container, announcements, options = {}) {
  const instance = createAnnouncementScroll(container, announcements, options);
  instance.start();
  return instance;
}

export default AnnouncementScroll;
